import React, { useEffect, useState } from 'react';
import { FaFilePdf, FaSpinner } from 'react-icons/fa';
import DownloadRequestModal from './DownloadRequestModal';
import API_URL from '../../config/api';
import {
  buildFreshUrl,
  CONTENT_SYNC_KEYS,
  NO_CACHE_JSON_HEADERS,
  subscribeToContentUpdates,
} from '../../utils/contentSync';
import './Catalog.css';

const getCatalogCoverUrl = (catalog) => {
  if (catalog.coverImageUrl) {
    return catalog.coverImageUrl;
  }

  if (!catalog.coverImage) {
    return '';
  }

  if (catalog.coverImage.startsWith('http://') || catalog.coverImage.startsWith('https://')) {
    return catalog.coverImage;
  }

  let imagePath = catalog.coverImage;

  if (imagePath.includes('storage')) {
    imagePath = `/${imagePath.substring(imagePath.indexOf('storage'))}`;
  } else if (!imagePath.startsWith('/')) {
    imagePath = `/storage/catalogs/${imagePath}`;
  }

  return `${API_URL.ORIGIN}${imagePath.replace(/\\/g, '/')}`;
};

const Catalog = () => {
  const [catalogs, setCatalogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCatalog, setSelectedCatalog] = useState(null);
  const [showEnquiryModal, setShowEnquiryModal] = useState(false);

  const fetchCatalogs = async () => {
    try {
      setLoading((current) => current && catalogs.length === 0);
      setError('');

      const response = await fetch(buildFreshUrl(API_URL.CATALOGS), {
        cache: 'no-store',
        headers: {
          ...NO_CACHE_JSON_HEADERS,
        },
      });

      if (!response.ok) {
        let message = `Failed to fetch catalogues: ${response.status}`;
        try {
          const body = await response.json();
          if (body?.message) message = body.message;
          else if (body?.error) message = body.error;
        } catch (_) {}
        throw new Error(message);
      }

      const data = await response.json();

      if (data.success) {
        setCatalogs(Array.isArray(data.data) ? data.data : []);
      } else {
        throw new Error(data.message || 'Failed to load catalogues.');
      }
    } catch (err) {
      console.error('Error fetching catalogues:', err);
      setError(err.message || 'Failed to load catalogues.');
      setCatalogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCatalogs();
  }, []);

  useEffect(
    () =>
      subscribeToContentUpdates({
        keys: [CONTENT_SYNC_KEYS.CATALOGS],
        onUpdate: fetchCatalogs,
      }),
    []
  );

  const openEnquiryModal = (catalog) => {
    setSelectedCatalog(catalog);
    setShowEnquiryModal(true);
  };

  const closeEnquiryModal = () => {
    setShowEnquiryModal(false);
    setSelectedCatalog(null);
  };

  if (loading) {
    return (
      <section className="catalog-page">
        <div className="catalog-shell">
          <div className="catalog-state">
            <FaSpinner className="catalog-state-spinner" />
            <p>Loading catalogues...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="catalog-page">
        <div className="catalog-shell">
          <div className="catalog-state catalog-state-error">
            <h2>Unable to load catalogues</h2>
            <p>{error}</p>
            <button type="button" className="catalog-secondary-btn" onClick={fetchCatalogs}>
              Try Again
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="catalog-page">
      <div className="catalog-shell">
        <div className="catalog-heading">
          <h1>Download Catalogue</h1>
          <div className="catalog-heading-line" />
        </div>

        {catalogs.length === 0 ? (
          <div className="catalog-state">
            <FaFilePdf className="catalog-empty-icon" />
            <h2>No catalogues available</h2>
            <p>Please add a catalogue cover from the admin panel.</p>
          </div>
        ) : (
          <div className="catalog-grid">
            {catalogs.map((catalog) => (
              <article key={catalog._id || catalog.id} className="catalog-card">
                <div className="catalog-card-frame">
                  <div className="catalog-card-imageWrap">
                    {getCatalogCoverUrl(catalog) ? (
                      <img
                        className="catalog-card-image"
                        src={getCatalogCoverUrl(catalog)}
                        alt={`${catalog.title} catalogue cover`}
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <div className="catalog-card-placeholder">Catalogue Cover</div>
                    )}
                  </div>
                </div>

                <h3 className="catalog-card-title">{catalog.title}</h3>

                <button
                  type="button"
                  className="catalog-primary-btn"
                  onClick={() => openEnquiryModal(catalog)}
                >
                  Download Catalogue
                </button>
              </article>
            ))}
          </div>
        )}
      </div>

      <DownloadRequestModal
        isOpen={showEnquiryModal}
        onClose={closeEnquiryModal}
        catalog={selectedCatalog}
      />
    </section>
  );
};

export default Catalog;
